import { FONT_SIZE_EXTRA_SMALL } from "reports/constants/constants";
import { Text } from "recharts";

/**
 * Text attributes for different positions references from recharts github:
 * https://github.com/recharts/recharts/blob/master/src/component/Label.tsx
 *  */

/**
 * Apply bar label with suffix to top of vertical bar
 *
 * @param {{ suffix?: string }} { suffix = '' }
 */
export const TopBarLabel =
  ({
    suffix = "",
    angle = 0,
    textColor = "",
    formatter,
    fontSize = FONT_SIZE_EXTRA_SMALL,
  }: {
    suffix?: string;
    angle?: number;
    textColor?: string;
    formatter?: (value: number) => string | number;
    fontSize?: number;
  }) =>
  props => {
    const { x, y, width, offset } = props;
    const formattedValue = formatter ? formatter(props.value) : props.value;
    const content = `${formattedValue}${suffix}`;

    return (
      <Text
        {...props}
        x={x + width / 2}
        y={y - 3 * offset}
        textAnchor="middle"
        verticalAnchor="start"
        fill={textColor ? textColor : "black"}
        fontSize={fontSize}
        angle={angle}
      >
        {content}
      </Text>
    );
  };

/**
 * Apply bar label with suffix to right of horizontal bar
 *
 * @param {{ suffix?: string }} { suffix = '' }
 */
export const RightBarLabel =
  ({
    suffix = "",
    fontSize = FONT_SIZE_EXTRA_SMALL,
  }: {
    suffix?: string;
    fontSize?: number;
  }) =>
  props => {
    const { x, y, width, height, value, offset } = props;
    const content = `${value}${suffix}`;

    return (
      <Text
        {...props}
        x={x + width + offset}
        y={y + height / 2}
        textAnchor="start"
        verticalAnchor="middle"
        fill="black"
        fontSize={fontSize}
      >
        {content}
      </Text>
    );
  };

/**
 * Apply bar label with suffix to inside right of horizontal bar
 *
 * @param {{ suffix?: string }} { suffix = '' }
 */
export const RightInsideBarLabel =
  ({
    suffix = "",
    fontSize = FONT_SIZE_EXTRA_SMALL,
    labelFormatter,
  }: {
    suffix?: string;
    fontSize?: number;
    /** use a custom formatter instead of a suffix */
    labelFormatter?: (value: number) => string;
  }) =>
  props => {
    const { x, y, width, height, value, offset } = props;
    const content = `${value}${suffix}`;
    const contentFormatted = labelFormatter ? labelFormatter(value) : content;

    return (
      <Text
        {...props}
        x={x + width - offset}
        y={y + height / 2}
        textAnchor="end"
        verticalAnchor="middle"
        fill="black"
        fontSize={fontSize}
      >
        {contentFormatted}
      </Text>
    );
  };

/**
 * Apply bar label with suffix to middle of vertical bar
 *
 * @param {{
 *     suffix?: string;
 *     angle?: number;
 *     color?: string;
 *     showValuesUnderZero?: boolean;
 *   }} {
 *     suffix = '',
 *     angle = 0,
 *     color = 'black',
 *     showValuesUnderZero = false,
 *   }
 */
export const MiddleBarLabel =
  ({
    suffix = "",
    angle = 0,
    color = "black",
    showValuesUnderZero = false,
    fontSize = FONT_SIZE_EXTRA_SMALL,
  }: {
    suffix?: string;
    angle?: number;
    color?: string;
    showValuesUnderZero?: boolean;
    fontSize?: number;
  }) =>
  props => {
    const { x, y, width, height, value, offset } = props;
    const displayValue = showValuesUnderZero
      ? `${value}${suffix}`
      : value > 0
        ? `${value}${suffix}`
        : "";

    return (
      <Text
        x={x + width / 2}
        y={y + height / 2 + offset}
        fill={color}
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={fontSize}
        angle={angle}
      >
        {displayValue}
      </Text>
    );
  };

/**
 * Apply bar label with suffix to inside bottom of vertical bar
 *
 * @param {{ suffix?: string }} { suffix = '' }
 */
export const InsideBottomBarLabel =
  ({
    suffix = "",
    angle = 0,
    color = "black",
    fontSize = FONT_SIZE_EXTRA_SMALL,
  }: {
    suffix?: string;
    angle?: number;
    color?: string;
    fontSize?: number;
  }) =>
  props => {
    const { x, y, width, height, value, offset } = props;
    const content = `${value}${suffix}`;

    return (
      <Text
        {...props}
        x={x + width / 2}
        y={y + height - offset}
        textAnchor="middle"
        verticalAnchor="end"
        fill={color}
        fontSize={fontSize}
        angle={angle}
      >
        {content}
      </Text>
    );
  };
